/**
 * Phase trigger helpers for spec 010 (v0.10.0-rc1) — wraps POST /files/{id}/process
 * and POST /files/{id}/blitz. Returns a discriminated union so callers can
 * branch on each documented response code without re-parsing the body:
 *
 *   { kind: 'accepted',       payload }  // 202 PhaseTriggerAccepted
 *   { kind: 'skipped',        payload }  // 200 PhaseTriggerSkipped
 *   { kind: 'voice_invalid',  payload }  // 422 VoiceValidationErrorBody (also refreshes the voice cache)
 *   { kind: 'conflict',       payload }  // 409 PhaseTriggerConflict
 *   { kind: 'precondition',   payload }  // 412 (e.g. Phase 2 not done)
 *   { kind: 'forbidden',      payload }  // 403
 *   { kind: 'not_found',      payload }  // 404
 *   { kind: 'error', status, payload }   // 400 / 401 / 500 — fall through to existing generic plumbing
 */

import axios from 'axios';
import { getApiUrl } from '../config';
import { getAccessToken } from './auth';
import * as voiceCatalogue from './voiceCatalogue';

/**
 * Build the request body the PhaseTriggerRequest schema accepts.
 * The FE only ever sends the `voice` field; per spec 010 FR-006 the
 * `force` field is API-only and intentionally NOT surfaced in the UI.
 * Direct API callers that need it must build the request body themselves.
 *
 * Rule: omit `voice` when empty so the server resolves it from the
 * file's snapshotted `tts_voice` / system default.
 */
export function buildTriggerBody({ voice } = {}) {
  const body = {};
  if (voice && voice.length > 0) body.voice = voice;
  return body;
}

// Map error-response status codes to discriminated-union `kind`s. Anything
// not in this table falls through to `{ kind: 'error', status }`.
const STATUS_TO_KIND = {
  409: 'conflict',
  412: 'precondition',
  403: 'forbidden',
  404: 'not_found',
};

async function handleError(err) {
  const status = err && err.response ? err.response.status : null;
  const data = err && err.response && err.response.data ? err.response.data : null;
  if (status === 422 && data) {
    try { await voiceCatalogue.refreshFromErrorBody(data); } catch (_) { /* non-fatal */ }
    return { kind: 'voice_invalid', payload: data };
  }
  const kind = STATUS_TO_KIND[status];
  if (kind) return { kind, payload: data };
  return { kind: 'error', status, payload: data };
}

async function dispatch(endpoint, projectId, body) {
  const token = getAccessToken();
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  try {
    const response = await axios.post(
      getApiUrl(`projects/${projectId}/${endpoint}`),
      body || {},
      { headers },
    );
    const status = response && response.status;
    const payload = (response && response.data) || null;
    return status === 200 ? { kind: 'skipped', payload } : { kind: 'accepted', payload };
  } catch (err) {
    return handleError(err);
  }
}

export function triggerProcess(projectId, body) {
  return dispatch('process', projectId, body);
}

export function triggerBlitz(projectId, body) {
  return dispatch('blitz', projectId, body);
}
